import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languages, changeLanguage } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { updateMe } from '../../services/authService';
import { LanguagePreference } from '../../types/auth';
import './LanguageSwitcher.css';

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation('common');
  const { isAuthenticated, updateUser, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (code: string) => {
    changeLanguage(code);
    setIsOpen(false);

    // Sync to backend if user is authenticated.
    // Also refresh AuthContext so components that read `user.languagePreference`
    // (e.g., SentenceDisplay in Stage4Validation / CourseDetailPage) pick up
    // the new value immediately, not just after a page refresh.
    if (isAuthenticated) {
      try {
        const updatedUser = await updateMe({ languagePreference: code as LanguagePreference });
        if (user) {
          updateUser({ ...user, ...updatedUser });
        }
      } catch (error) {
        // Silently fail - local change is still applied
        console.error('Failed to sync language preference:', error);
      }
    }
  };

  return (
    <div className="language-switcher" ref={dropdownRef}>
      <button
        className="language-switcher-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('switchLanguage')}
      >
        <GlobeIcon />
        <span className="language-current">{currentLanguage.flag}</span>
        <svg
          className={`language-chevron ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
        >
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </button>

      {isOpen && (
        <div className="language-dropdown">
          {[...languages].sort((a, b) => {
            // Current language first
            if (a.code === i18n.language) return -1;
            if (b.code === i18n.language) return 1;
            return 0;
          }).map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${i18n.language === lang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <span className="language-flag">{lang.flag}</span>
              <span className="language-name">{lang.name}</span>
              {i18n.language === lang.code && (
                <svg className="language-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
